import type {
  CustomField,
  FieldConfig,
  ResolvedField,
  SystemFieldDefinition
} from '@gat-ship/types'
import { findConfig } from './field-resolution'

/** Resolve a single system field into an admin builder row (config overrides defaults). */
function resolveSystemFieldRow(
  sf: SystemFieldDefinition,
  config: FieldConfig | undefined,
  sortOrder: number
): ResolvedField {
  return {
    fieldName: sf.fieldName,
    label: sf.label,
    inputType: null,
    fieldType: sf.fieldType ?? null,
    origin: 'system',
    configurable: sf.configurable,
    requiredByDeveloper: sf.requiredByDeveloper,
    hint: config?.hint ?? null,
    requiredOption: sf.requiredByDeveloper
      ? 'required'
      : (config?.requiredOption ?? 'optional'),
    visibilityOption: sf.requiredByDeveloper
      ? 'visible'
      : (config?.visibilityOption ?? 'visible'),
    visibleForRoleIds: config?.visibleForRoleIds ?? null,
    editableOption: config?.editableOption ?? 'editable',
    editableForRoleIds: config?.editableForRoleIds ?? null,
    numberOfDecimals: null,
    dropdownOptions: null,
    aiDescription: null,
    customFieldId: null,
    sortOrder,
    hasConfigOverride: !!config
  }
}

/** Resolve a single custom field into an admin builder row. */
function resolveCustomFieldRow(cf: CustomField): ResolvedField {
  return {
    fieldName: cf.name,
    label: cf.label,
    inputType: cf.inputType,
    fieldType: null,
    origin: 'custom',
    configurable: true,
    requiredByDeveloper: false,
    hint: cf.hint,
    requiredOption: cf.requiredOption,
    visibilityOption: cf.visibilityOption,
    visibleForRoleIds: cf.visibleForRoleIds,
    editableOption: cf.editableOption,
    editableForRoleIds: cf.editableForRoleIds,
    numberOfDecimals: cf.numberOfDecimals,
    dropdownOptions: cf.dropdownOptions,
    aiDescription: cf.aiDescription,
    customFieldId: cf.id,
    sortOrder: cf.sortOrder,
    hasConfigOverride: false
  }
}

/**
 * Merge system fields (definition order, with config override) and custom
 * fields (sorted by `sortOrder`) into the admin builder's resolved list.
 */
export function resolveAdminFields(
  systemFields: readonly SystemFieldDefinition[],
  customFields: readonly CustomField[],
  configs: readonly FieldConfig[]
): ResolvedField[] {
  const resolved: ResolvedField[] = []

  // System fields in definition order
  systemFields.forEach((sf, i) => {
    resolved.push(
      resolveSystemFieldRow(sf, findConfig(configs, sf.fieldName, 'system'), i)
    )
  })

  // Custom fields sorted by sortOrder
  const sorted = [...customFields].sort((a, b) => a.sortOrder - b.sortOrder)
  for (const cf of sorted) {
    resolved.push(resolveCustomFieldRow(cf))
  }

  return resolved
}
